import { loadGSAP } from '../../externals/gsap.js';

export const defaults = {
  duration: 0.5,
  ease: 'power2.out',
};

let groupCounter = 0;

export default async function accordion(el, options) {
  await loadGSAP();

  const settings = {
    duration: parseFloat(el.dataset.bolkAccordionDuration || options.duration),
    ease: el.dataset.bolkAccordionEase || options.ease,
  };

  initGroup(el, settings);

  if (el.getAttribute('data-bolk-accordion-autoplay') === 'true') {
    initAutoplay(el);
  }
}

function initGroup(accordionGroup, settings) {
  const groupIndex = ++groupCounter;
  const accordions = accordionGroup.querySelectorAll('[data-bolk-accordion="accordion"]');
  const isSingle = accordionGroup.getAttribute('data-bolk-accordion-single') === 'true';
  const initialIndexes = (accordionGroup.getAttribute('data-bolk-accordion-initial') || '')
    .split(',').map(Number).filter(n => !isNaN(n));
  const isLock = accordionGroup.getAttribute('data-bolk-accordion-lock') === 'true';
  const isHorizontal = accordionGroup.getAttribute('data-bolk-accordion-direction') === 'horizontal';
  const axis = isHorizontal ? 'width' : 'height';

  accordions.forEach((accordion, index) => {
    const trigger = accordion.querySelector('[data-bolk-accordion="trigger"]');
    const content = accordion.querySelector('[data-bolk-accordion="content"]');
    const arrow = trigger?.querySelector('[data-bolk-accordion-arrow]');

    if (!trigger || !content) return;

    const contentId = `accordion-content-${groupIndex}-${index + 1}`;
    const triggerId = `accordion-trigger-${groupIndex}-${index + 1}`;
    trigger.setAttribute('id', triggerId);
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-controls', contentId);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('tabindex', '0');
    content.setAttribute('id', contentId);
    content.setAttribute('aria-labelledby', triggerId);
    content.setAttribute('aria-hidden', 'true');
    content.style.overflow = 'hidden';

    const isInitiallyOpen = initialIndexes.includes(index + 1);
    if (isInitiallyOpen) {
      accordion.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');
      content.style[axis] = 'auto';
      content.style.opacity = '1';
      if (arrow) window.gsap.set(arrow, { rotate: 180 });
    } else {
      window.gsap.set(content, { [axis]: 0, opacity: 0 });
    }

    function openAccordion(acc) {
      const accContent = acc.querySelector('[data-bolk-accordion="content"]');
      const accTrigger = acc.querySelector('[data-bolk-accordion="trigger"]');
      const accArrow = accTrigger.querySelector('[data-bolk-accordion-arrow]');

      acc.classList.add('is-active');
      accTrigger.setAttribute('aria-expanded', 'true');
      accContent.setAttribute('aria-hidden', 'false');

      accContent.style[axis] = 'auto';
      const fullSize = isHorizontal ? accContent.scrollWidth : accContent.scrollHeight;
      accContent.style[axis] = '0px';

      window.gsap.to(accContent, {
        [axis]: fullSize,
        opacity: 1,
        duration: settings.duration,
        ease: settings.ease,
        onComplete: () => { accContent.style[axis] = 'auto'; },
      });

      if (accArrow) window.gsap.to(accArrow, { rotate: 180, duration: 0.3, ease: 'power1.out' });
    }

    function closeAccordion(acc) {
      const accContent = acc.querySelector('[data-bolk-accordion="content"]');
      const accTrigger = acc.querySelector('[data-bolk-accordion="trigger"]');
      const accArrow = accTrigger.querySelector('[data-bolk-accordion-arrow]');

      acc.classList.remove('is-active');
      accTrigger.setAttribute('aria-expanded', 'false');
      accContent.setAttribute('aria-hidden', 'true');

      const currentSize = isHorizontal ? accContent.scrollWidth : accContent.scrollHeight;
      window.gsap.fromTo(accContent,
        { [axis]: currentSize, opacity: 1 },
        { [axis]: 0, opacity: 0, duration: settings.duration, ease: settings.ease }
      );

      if (accArrow) window.gsap.to(accArrow, { rotate: 0, duration: 0.3, ease: 'power1.out' });
    }

    function toggleAccordion() {
      const isOpen = accordion.classList.contains('is-active');
      if (isSingle && !isOpen) {
        accordions.forEach(other => {
          if (other !== accordion && other.classList.contains('is-active')) closeAccordion(other);
        });
      } else if (isSingle && isOpen && isLock) {
        return;
      }
      isOpen ? closeAccordion(accordion) : openAccordion(accordion);
    }

    trigger.addEventListener('click', toggleAccordion);
    trigger.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAccordion(); }
    });
  });
}

// ── Autoplay ──────────────────────────────────────────────────────────────────

function initAutoplay(group) {
  const mq = window.matchMedia('(min-width: 768px)');
  let instance = null;

  const mount   = () => { if (!instance && mq.matches) instance = new AutoplayAccordion(group); };
  const unmount = () => { instance?.destroy(); instance = null; };
  const onBreakpoint = () => mq.matches ? mount() : unmount();

  mount();
  mq.addEventListener ? mq.addEventListener('change', onBreakpoint) : mq.addListener(onBreakpoint);
}

class AutoplayAccordion {
  constructor(group) {
    this.group = group;
    this.delay = parseFloat(group.dataset.bolkAccordionAutoplayDelay || '5');
    this.items = [...group.querySelectorAll('[data-bolk-accordion="accordion"]')];
    if (!this.items.length) return;

    this.triggers = this.items.map(item => item.querySelector('[data-bolk-accordion="trigger"]'));
    this.bars     = this.items.map(item => item.querySelector('[data-bolk-accordion-progress]'));
    this.activeIndex   = -1;
    this.progressTween = null;

    this.onClick = e => {
      if (!e.target.closest('[data-bolk-accordion="trigger"]')) return;
      setTimeout(() => this.sync(true), 60);
    };

    this.group.addEventListener('click', this.onClick);
    this.resetAllBars();
    this.sync(true);
  }

  getActiveIndex() {
    let i = this.triggers.findIndex(t => t?.getAttribute('aria-expanded') === 'true');
    if (i > -1) return i;
    return this.items.findIndex(item => item.classList.contains('is-active'));
  }

  resetAllBars() { window.gsap.set(this.bars.filter(Boolean), { width: '0%' }); }

  sync(force = false) {
    const index = this.getActiveIndex();
    if (index < 0) { this.openIndex(0); return; }
    const changed = index !== this.activeIndex;
    this.activeIndex = index;
    if (changed || force) this.startProgress(index);
  }

  startProgress(index) {
    this.progressTween?.kill();
    this.resetAllBars();
    const bar = this.bars[index];
    if (!bar) return;
    this.progressTween = window.gsap.fromTo(bar,
      { width: '0%' },
      { width: '100%', duration: this.delay, ease: 'none', onComplete: () => this.openNext() }
    );
  }

  openNext() {
    this.openIndex((this.activeIndex + 1) % this.items.length);
  }

  openIndex(index) { this.triggers[index]?.click(); }

  destroy() {
    this.progressTween?.kill();
    this.group.removeEventListener('click', this.onClick);
    this.resetAllBars();
  }
}
